import {
	getFormProps,
	getInputProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import type { ActionFunctionArgs, LoaderFunctionArgs } from '@remix-run/node'
import {
	Form,
	json,
	redirect,
	useActionData,
	useLoaderData,
} from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { brandUrl } from '#app/components/branding.js'
import DiscordContact from '#app/components/discord-contact.js'
import {
	ErrorList,
	Field,
	FieldSelect,
	TextareaField,
} from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.js'
import { getUserId } from '#app/utils/auth.server.js'
import { ContactFormSchema, contactCategories } from '#app/utils/contact.helper.js'
import { createContactSubmission } from '#app/utils/contact.server.js'
import { createConversation } from '#app/utils/conversation.server.js'
import { prisma } from '#app/utils/db.server.js'
import { checkHoneypot } from '#app/utils/honeypot.server.js'
import { checkSpam } from '#app/utils/spam.server.js'
import { useIsPending } from '#app/utils/misc.js'
import { getServerNameList } from '#app/utils/shards.server.js'
import { useOptionalUser } from '#app/utils/user.js'
import {
	Combobox,
	ComboboxInput,
	ComboboxOption,
	ComboboxOptions,
} from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { useState } from 'react'

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const userId = await getUserId(request)
	let user = null
	if (userId) {
		user = await prisma.user.findUnique({
			where: { id: userId },
			select: { name: true, username: true, email: true },
		})
	}
	return json({ serverList: await getServerNameList(), user })
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	checkHoneypot(formData)

	const userId = await getUserId(request)
	let user = null
	if (userId) {
		user = await prisma.user.findUnique({
			where: { id: userId },
			select: { name: true, username: true, email: true },
		})
	}

	// If logged in, override name/email from user account
	if (user) {
		formData.set('name', user.name ?? user.username)
		formData.set('email', user.email)
	}

	// Time-based bot check: reject if form submitted in under 3 seconds
	const formTimestamp = formData.get('_formLoadedAt')
	if (typeof formTimestamp === 'string') {
		const elapsed = Date.now() - Number(formTimestamp)
		if (elapsed < 3000) {
			throw new Response('Form not submitted properly', { status: 400 })
		}
	}

	const submission = parseWithZod(formData, { schema: ContactFormSchema })

	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply(), success: false },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	// Spam heuristics for anonymous submissions
	if (!userId) {
		const { isSpam } = checkSpam({
			name: submission.value.name,
			email: submission.value.email,
			subject: submission.value.subject,
			message: submission.value.message,
		})
		if (isSpam) {
			// Silent rejection — return success so bots don't retry
			return json({
				result: submission.reply({ resetForm: true }),
				success: true,
			})
		}
	}

	// Signed-in users get a threaded conversation; anonymous users get a one-shot submission
	if (userId) {
		const conversation = await createConversation({
			subject: submission.value.subject,
			category: submission.value.category,
			server: submission.value.server,
			userId,
			message: submission.value.message,
		})
		return redirect(`/support/${conversation.id}`)
	}

	await createContactSubmission(submission.value)

	return json({
		result: submission.reply({ resetForm: true }),
		success: true,
	})
}

export default function ContactPage() {
	const { serverList, user } = useLoaderData<typeof loader>()
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const optionalUser = useOptionalUser()
	const isLoggedIn = !!optionalUser

	const [formLoadedAt] = useState(() => Date.now())
	const [category, setCategory] = useState<string | null>(null)
	const [selectedServer, setSelectedServer] = useState<string | null>(null)
	const [queryServer, setQueryServer] = useState('')

	const filteredServers =
		queryServer === ''
			? serverList
			: serverList.filter((server) =>
					server.toLowerCase().includes(queryServer.toLowerCase()),
				)

	const [form, fields] = useForm({
		id: 'contact-form',
		constraint: getZodConstraint(ContactFormSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			if (isLoggedIn && user) {
				formData.set('name', user.name ?? user.username ?? 'User')
				formData.set('email', user.email)
			}
			return parseWithZod(formData, { schema: ContactFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="container mb-48 mt-6 flex flex-col">
			<div className="flex flex-wrap">
				<h1 className="text-2xl font-bold md:text-4xl uo-label uo-plaque">Contact {brandUrl}</h1>
			</div>

			<p className="mt-2 text-muted-foreground">
				Have a question, suggestion, or feedback? Send us a message
				below.
			</p>

			<div className="container mt-4">
				<DiscordContact />
			</div>

			{!isLoggedIn && actionData?.success ? (
				<div className="container mt-4 rounded-3xl bg-muted p-6 md:p-12">
					<div className="text-center">
						<h2 className="text-xl font-semibold text-green-600 dark:text-green-400">
							Message sent!
						</h2>
						<p className="mt-2 text-muted-foreground">
							Thanks for reaching out. We'll get back to you as
							soon as we can.
						</p>
						<p className="mt-2 text-sm text-muted-foreground">
							Tip: Signed-in users get faster responses and can
							track their conversations.
						</p>
					</div>
				</div>
			) : (
				<div className="container mt-4 flex flex-col rounded-3xl bg-muted p-6 md:p-12">
					{isLoggedIn ? (
						<p className="mb-4 text-sm text-muted-foreground">
							Sending as{' '}
							<span className="font-medium text-foreground">
								{user?.name ?? optionalUser?.username}
							</span>{' '}
						</p>
					) : (
						<p className="mb-4 text-sm text-muted-foreground">
							Signed-in users get faster responses.{' '}
							<a
								href="/login"
								className="underline hover:no-underline"
							>
								Log in
							</a>{' '}
							or{' '}
							<a
								href="/signup"
								className="underline hover:no-underline"
							>
								create an account
							</a>{' '}
							for priority support.
						</p>
					)}

					<Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />
						<input type="hidden" name="_formLoadedAt" value={formLoadedAt} />

						{!isLoggedIn && (
							<div className="flex flex-col gap-4 md:flex-row">
								<Field
									className="md:w-1/2"
									labelProps={{
										htmlFor: fields.name.id,
										children: 'Your Name',
									}}
									inputProps={{
										...getInputProps(fields.name, {
											type: 'text',
										}),
										placeholder: 'Enter your name',
										autoComplete: 'name',
									}}
									errors={fields.name.errors}
								/>
								<Field
									className="md:w-1/2"
									labelProps={{
										htmlFor: fields.email.id,
										children: 'Email Address',
									}}
									inputProps={{
										...getInputProps(fields.email, {
											type: 'email',
										}),
										placeholder: 'you@example.com',
										autoComplete: 'email',
									}}
									errors={fields.email.errors}
								/>
							</div>
						)}

						<div className="flex flex-col gap-4 md:flex-row">
							<Field
								className="md:w-1/3"
								labelProps={{
									htmlFor: fields.subject.id,
									children: 'Subject',
								}}
								inputProps={{
									...getInputProps(fields.subject, {
										type: 'text',
									}),
									autoFocus: true,
									placeholder: 'What is this about?',
									autoComplete: 'off',
								}}
								errors={fields.subject.errors}
							/>
							<FieldSelect
								className="md:w-1/3"
								labelProps={{
									htmlFor: fields.category.id,
									children: 'Category (optional)',
								}}
								options={[...contactCategories]}
								onChange={setCategory}
								selectProps={{
									key: fields.category.id,
									name: 'category',
									form: form.id,
									value: category ?? '',
									placeholder: 'Select a category',
								}}
								errors={fields.category.errors}
							/>
							<div className="md:w-1/3">
								<label
									htmlFor="server"
									className="text-sm font-medium leading-none"
								>
									Server (optional)
								</label>
								<Combobox
									immediate
									name="server"
									value={selectedServer}
									onChange={setSelectedServer}
									onClose={() => setQueryServer('')}
								>
									<ComboboxInput
										id="server"
										aria-label="server"
										placeholder="Select a server"
										autoComplete="off"
										className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
										displayValue={(server: string) =>
											server ?? ''
										}
										onChange={(event) =>
											setQueryServer(event.target.value)
										}
									/>
									<ComboboxOptions
										anchor="bottom start"
										transition
										className="origin-top border transition duration-200 ease-out empty:invisible data-[closed]:scale-95 data-[closed]:opacity-0"
									>
										{filteredServers.map((server) => (
											<ComboboxOption
												key={server}
												value={server}
												className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[focus]:bg-blue-500"
											>
												{({ selected }) => (
													<div
														className={clsx(
															'group flex gap-2',
														)}
													>
														{selected && (
															<CheckIcon className="size-5" />
														)}
														{server}
													</div>
												)}
											</ComboboxOption>
										))}
									</ComboboxOptions>
								</Combobox>
							</div>
						</div>

						<TextareaField
							className="mt-2"
							labelProps={{
								children: 'Message',
							}}
							textareaProps={{
								...getTextareaProps(fields.message),
								placeholder:
									'Tell us what you need help with or share your feedback...',
								rows: 8,
							}}
							errors={fields.message.errors}
						/>

						<ErrorList errors={form.errors} id={form.errorId} />

						<StatusButton
							className="mt-4 w-full"
							status={
								isPending
									? 'pending'
									: (form.status ?? 'idle')
							}
							type="submit"
							disabled={isPending}
						>
							{isLoggedIn
								? 'Start Conversation'
								: 'Send Message'}
						</StatusButton>
					</Form>
				</div>
			)}
		</div>
	)
}
